import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, MessageSquare, Send } from 'lucide-react';
import { messagingApi, type ConversationListItem, type InboxMessage } from '@/lib/api/messaging.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastInfo, toastSuccess } from '@/lib/toast.js';
import { formatRelativeTime } from '@/lib/format.js';
import { useAuthStore } from '@/stores/auth.js';
import { createReconnectingWebSocket } from '@/lib/ws-reconnect.js';

type Filter = 'all' | 'sms' | 'unread';

function formatPhone(phone: string | null | undefined) {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  return phone;
}

export function MessagesPage() {
  const queryClient = useQueryClient();
  const storeId = useAuthStore((s) => s.user?.storeId);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(
    searchParams.get('conversation'),
  );
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [compose, setCompose] = useState('');
  const threadEndRef = useRef<HTMLDivElement>(null);

  const listQuery = useQuery({
    queryKey: ['messaging-conversations', search, filter],
    queryFn: () =>
      messagingApi.listConversations({
        search: search.trim() || undefined,
        channel: filter === 'sms' ? 'sms' : undefined,
        status: filter === 'unread' ? 'open' : undefined,
      }),
  });

  const conversations = useMemo(() => {
    const rows = listQuery.data?.data ?? [];
    if (filter !== 'unread') return rows;
    return rows.filter((c) => c.unreadCount > 0);
  }, [listQuery.data?.data, filter]);

  useEffect(() => {
    const fromUrl = searchParams.get('conversation');
    if (fromUrl) setSelectedId(fromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (!selectedId && conversations[0]) setSelectedId(conversations[0].id);
  }, [conversations, selectedId]);

  const detailQuery = useQuery({
    queryKey: ['messaging-conversation', selectedId],
    queryFn: () => messagingApi.getConversation(selectedId!),
    enabled: Boolean(selectedId),
  });

  const sendMut = useMutation({
    mutationFn: () => messagingApi.sendMessage(selectedId!, compose.trim()),
    onMutate: async () => {
      const body = compose.trim();
      setCompose('');
      if (!selectedId) return;
      await queryClient.cancelQueries({ queryKey: ['messaging-conversation', selectedId] });
      const prev = queryClient.getQueryData(['messaging-conversation', selectedId]);
      queryClient.setQueryData(['messaging-conversation', selectedId], (old: unknown) => {
        const data = old as { data: { messages: InboxMessage[]; conversation: ConversationListItem } } | undefined;
        if (!data?.data) return old;
        const optimistic: InboxMessage = {
          id: `temp-${Date.now()}`,
          conversationId: selectedId,
          ticketId: data.data.conversation.ticketId,
          direction: 'outbound',
          channel: 'sms',
          fromAddress: '',
          toAddress: data.data.conversation.customerPhone ?? '',
          body,
          status: 'sending',
          isAutomated: false,
          readAt: null,
          createdAt: new Date().toISOString(),
          sentBy: null,
        };
        return {
          ...data,
          data: {
            ...data.data,
            messages: [...data.data.messages, optimistic],
          },
        };
      });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev && selectedId) {
        queryClient.setQueryData(['messaging-conversation', selectedId], ctx.prev);
      }
      toastError(parseApiError(e).message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messaging-conversation', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['messaging-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messaging-unread-count'] });
    },
  });

  const resolveMut = useMutation({
    mutationFn: () => messagingApi.resolve(selectedId!),
    onSuccess: () => {
      toastSuccess('Conversation resolved');
      queryClient.invalidateQueries({ queryKey: ['messaging-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messaging-conversation', selectedId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const linkMut = useMutation({
    mutationFn: (ticketId: string) => messagingApi.linkTicket(selectedId!, ticketId),
    onSuccess: () => {
      toastSuccess('Conversation linked to ticket');
      queryClient.invalidateQueries({ queryKey: ['messaging-conversation', selectedId] });
      queryClient.invalidateQueries({ queryKey: ['messaging-conversations'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  useEffect(() => {
    if (!storeId) return;
    const socket = createReconnectingWebSocket({
      path: `/ws/messages/${storeId}`,
      onMessage: (event) => {
        try {
          const msg = JSON.parse(event.data as string) as {
            type?: string;
            payload?: { conversationId?: string; customerName?: string; preview?: string };
          };
          if (msg.type === 'message.received') {
            const convId = msg.payload?.conversationId;
            if (convId && convId === selectedId) {
              queryClient.invalidateQueries({ queryKey: ['messaging-conversation', selectedId] });
            } else {
              toastInfo(
                `New message from ${msg.payload?.customerName ?? 'customer'}: ${msg.payload?.preview ?? ''}`,
                { duration: 5000 },
              );
            }
          }
        } catch {
          /* ignore */
        }
        queryClient.invalidateQueries({ queryKey: ['messaging-conversations'] });
        queryClient.invalidateQueries({ queryKey: ['messaging-unread-count'] });
      },
    });
    return () => socket.close();
  }, [storeId, selectedId, queryClient]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detailQuery.data?.data?.messages?.length]);

  const detail = detailQuery.data?.data;
  const selected = conversations.find((c) => c.id === selectedId);
  const openTickets = detail?.openTickets ?? [];

  function selectConversation(id: string) {
    setSelectedId(id);
    setSearchParams({ conversation: id }, { replace: true });
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <aside className="flex w-80 shrink-0 flex-col border-r border-slate-200">
        <div className="border-b border-slate-200 p-4">
          <h1 className="text-lg font-bold text-slate-950">Messages</h1>
          <input
            className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            {(['all', 'sms', 'unread'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                className={`rounded-lg px-3 py-1 text-xs font-semibold capitalize ${
                  filter === f ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'
                }`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'sms' ? 'SMS' : 'Unread'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.map((conv) => (
            <ConversationRow
              key={conv.id}
              conv={conv}
              active={conv.id === selectedId}
              onClick={() => selectConversation(conv.id)}
            />
          ))}
          {!listQuery.isLoading && conversations.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">No conversations yet.</p>
          ) : null}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        {!selectedId || !detail ? (
          <div className="flex flex-1 items-center justify-center text-slate-500">
            <MessageSquare className="mr-2 h-5 w-5" />
            Select a conversation
          </div>
        ) : (
          <>
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
              <div>
                <h2 className="text-lg font-bold text-slate-950">
                  {detail.customer
                    ? `${detail.customer.firstName} ${detail.customer.lastName ?? ''}`.trim()
                    : selected?.customerName ?? 'Unknown'}
                </h2>
                <p className="text-sm text-slate-500">
                  {formatPhone(detail.conversation.customerPhone)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {detail.ticket ? (
                  <Link
                    to={`/tickets/${detail.ticket.id}`}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                  >
                    {detail.ticket.ticketNumber}
                  </Link>
                ) : null}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={resolveMut.isPending || detail.conversation.status === 'resolved'}
                  onClick={() => resolveMut.mutate()}
                >
                  <Check className="h-4 w-4" />
                  Resolve
                </button>
              </div>
            </header>

            {!detail.ticket && openTickets.length > 1 ? (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <p className="font-semibold">
                  This customer has {openTickets.length} open tickets — which is this about?
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {openTickets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold ring-1 ring-amber-300 hover:bg-amber-100"
                      disabled={linkMut.isPending}
                      onClick={() => linkMut.mutate(t.id)}
                    >
                      {t.ticketNumber}
                    </button>
                  ))}
                  <span className="rounded-lg px-3 py-1.5 text-xs text-amber-800">
                    Not ticket related
                  </span>
                </div>
              </div>
            ) : null}

            <div className="flex-1 space-y-3 overflow-y-auto p-4">
              {detail.messages.map((msg) => {
                const outbound = msg.direction === 'outbound';
                return (
                  <div
                    key={msg.id}
                    className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                        outbound
                          ? 'bg-primary text-white'
                          : 'border border-slate-200 bg-slate-50 text-slate-800'
                      }`}
                    >
                      <div className="mb-1 flex items-center gap-2 text-xs opacity-80">
                        <span>{outbound ? 'Sent' : 'Received'}</span>
                        {msg.isAutomated ? <span className="font-bold">[AUTO]</span> : null}
                        <span>{formatRelativeTime(msg.createdAt)}</span>
                      </div>
                      <p className="whitespace-pre-wrap">{msg.body}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={threadEndRef} />
            </div>

            <footer className="border-t border-slate-200 p-4">
              <p className="mb-2 text-xs text-slate-500">
                Sending as SMS to {formatPhone(detail.conversation.customerPhone)}
              </p>
              <textarea
                className="min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary"
                placeholder="Type a message..."
                value={compose}
                onChange={(e) => setCompose(e.target.value)}
                maxLength={1600}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && compose.trim()) {
                    e.preventDefault();
                    sendMut.mutate();
                  }
                }}
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-slate-500">{compose.length} / 1600</span>
                <button
                  type="button"
                  disabled={!compose.trim() || sendMut.isPending}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={() => sendMut.mutate()}
                >
                  <Send className="h-4 w-4" />
                  {sendMut.isPending ? 'Sending…' : 'Send →'}
                </button>
              </div>
            </footer>
          </>
        )}
      </section>
    </div>
  );
}

function ConversationRow({
  conv,
  active,
  onClick,
}: {
  conv: ConversationListItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`w-full border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 ${
        active ? 'bg-blue-50/60' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {conv.unreadCount > 0 ? (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          ) : null}
          <span className="font-semibold text-slate-900">
            {conv.customerName ?? conv.customerPhone ?? 'Unknown'}
          </span>
        </div>
        <span className="shrink-0 text-xs text-slate-500">
          {conv.lastMessageAt ? formatRelativeTime(conv.lastMessageAt) : ''}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">📱 {conv.channel.toUpperCase()}</p>
      <p className="mt-1 truncate text-sm text-slate-600">
        {conv.lastMessagePreview ? `"${conv.lastMessagePreview}"` : 'No messages'}
        {conv.ticketNumber ? ` — Re: ${conv.ticketNumber}` : ''}
      </p>
    </button>
  );
}
